'use es6';

import { Component } from 'react';
import { List } from 'immutable';
import styled from 'styled-components';
import I18n from 'I18n';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Box } from 'foundations-components/transitional/components/Box';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { InfoIcon } from 'foundations-assets';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { stepGradientColorsArray } from '../../color/interpolate';
import { useTheme } from 'foundations-theming-specialty/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FunnelBarRow = ({
  header = false,
  children
}) => /*#__PURE__*/_jsx(Flex, {
  blockSize: "100%",
  inlineSize: "100%",
  flexGrow: header ? 0 : 1,
  minBlockSize: "30px",
  children: children
});
const ReportingChevron = styled.div.withConfig({
  displayName: "OldFunnelComponent__ReportingChevron",
  componentId: "sc-1yz1o5e-0"
})(["top:0;height:100%;width:100%;&::before{content:'';position:absolute;top:0;left:0;height:100%;width:51%;background-color:", ";transform:skew(0deg,12deg);}&::after{content:'';position:absolute;top:0;right:0;height:100%;width:50%;background-color:", ";transform:skew(0deg,-12deg);}"], ({
  theme
}) => theme.color['fill-tertiary-default'], ({
  theme
}) => theme.color['fill-tertiary-default']);
const ConversionColumn = ({
  children,
  onClick
}) => /*#__PURE__*/_jsx(Flex, {
  blockSize: "100%",
  inlineSize: "110px",
  flexDirection: "column",
  paddingInline: "300",
  onClick: onClick,
  children: children
});
const FunnelBarBand = styled.div.withConfig({
  displayName: "OldFunnelComponent__FunnelBarBand",
  componentId: "sc-1yz1o5e-1"
})(["display:flex;align-items:center;margin-inline-end:", ";position:absolute;border-radius:", ";inset-block-start:0;inset-block-end:0;inset-inline-end:", ";inline-size:", ";background-color:", ";"], ({
  outerValue,
  theme
}) => theme.space[outerValue ? '200' : '0'], ({
  theme
}) => theme.borderRadius['100'], ({
  right
}) => right, ({
  width
}) => width, ({
  backgroundColor
}) => backgroundColor);
const chevronTooltipLangKey = {
  skipStageCount: 'reporting-visualizations.funnel.tooltips.skipStageCount'
};
class FunnelClassComponent extends Component {
  constructor(props) {
    super(props);
    this.getBase = rows => {
      return rows.reduce((base, row) => {
        const {
          value
        } = row.values[1];
        return Math.max(base, value || 0);
      }, rows[0].values[1].value);
    };
    this.mapData = (dataset, filterProperty, valueProperty, primaryChevronProperty, secondaryChevronProperty) => {
      return dataset.get('data', List()).map(bucket => ({
        key: bucket.get(filterProperty),
        values: [{
          key: bucket.get(filterProperty),
          value: dataset.getIn(['properties', filterProperty, 'references', bucket.get(filterProperty), 'label'])
        }, {
          key: valueProperty,
          value: bucket.get(valueProperty),
          formatted: dataset.getIn(['properties', valueProperty, 'references', String(bucket.get(valueProperty)), 'label'])
        }, {
          key: primaryChevronProperty,
          value: bucket.get(primaryChevronProperty),
          formatted: dataset.getIn(['properties', primaryChevronProperty, 'references', String(bucket.get(primaryChevronProperty)), 'label'])
        }, {
          key: secondaryChevronProperty,
          value: bucket.get(secondaryChevronProperty),
          formatted: dataset.getIn(['properties', secondaryChevronProperty, 'references', String(bucket.get(secondaryChevronProperty)), 'label'])
        }],
        datasetRow: bucket
      })).toJS();
    };
    this.omitRepeats = data => data.reduce((uniqueData, bucket) => uniqueData.map(b => b.key).includes(bucket.key) ? uniqueData : [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    ...uniqueData, bucket], []);
    this.renderConversion = ({
      value,
      formatted
    }) => {
      return /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        minBlockSize: "28px",
        marginBlockStart: "300",
        children: [/*#__PURE__*/_jsx(TFlex, {
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          position: "absolute",
          marginBlockStart: "100",
          zIndex: "1",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "heading-50",
            children: value !== undefined && formatted
          })
        }), value !== undefined &&
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(ReportingChevron, {})]
      });
    };
    this.handleDrilldown = (row, column) => {
      const {
        visualization
      } = this.props;
      const datasetKey = visualization.get('dataset');
      const segment = row.key;
      if (visualization.events && visualization.events.onPointClick) {
        visualization.events.onPointClick({
          visualization,
          segment,
          dataset: visualization.getIn(['data', datasetKey]),
          row: row.datasetRow,
          field: column
        });
      }
    };
    this.renderFunnelBar = (ratio, color = '#9784c2', dataLabel) => {
      const offset = Math.floor(ratio * 100);
      const style = {
        width: `${offset || 1}%`,
        right: 0,
        backgroundColor: color
      };
      const valueStyle = {
        right: `${offset || 1}%`
      };
      if (offset < 25) {
        return /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(FunnelBarBand, Object.assign({
            outerValue: true
          }, valueStyle, {
            children: dataLabel
          })), /*#__PURE__*/_jsx(FunnelBarBand, Object.assign({}, style))]
        });
      }

      // @bend-expect trellis-tokenized
      return /*#__PURE__*/_jsx(FunnelBarBand, Object.assign({}, style, {
        children: /*#__PURE__*/_jsx(Text, {
          paddingInlineStart: "200",
          use: "on-dark",
          children: dataLabel
        })
      }));
    };
    this.renderList = visualization => {
      const datasetKey = visualization.get('dataset');
      const dataset = visualization.getIn(['data', datasetKey]);
      const filterProperty = visualization.get('filterProperty');
      const valueProperty = visualization.get('valueProperty');
      const primaryChevronProperty = visualization.get('primaryChevronProperty');
      const secondaryChevronProperty = visualization.get('secondaryChevronProperty');
      const baseRows = this.mapData(dataset, filterProperty, valueProperty, primaryChevronProperty, secondaryChevronProperty);
      const rows = this.omitRepeats(baseRows);
      const {
        startColor,
        endColor
      } = this.props.themeColors || {};
      const colors = stepGradientColorsArray(10, [{
        positionFraction: 0,
        value: startColor
      }, {
        positionFraction: 1,
        value: endColor
      }]);
      const base = this.getBase(rows);
      const countOfStages = dataset.get('data').size;
      const hasConversionSteps = rows.some((row, index) => {
        return rows[index].values[2].formatted || rows[index].values[3].formatted;
      });
      return rows.map((row, index) => {
        const {
          value,
          formatted
        } = row.values[1];
        const ratio = base === 0 ? 0 : value / base;
        const primaryChevron = rows[index].values[2].formatted ? this.renderConversion(rows[index].values[2]) : null;
        const secondaryChevron = rows[index].values[3].formatted ? this.renderConversion(rows[index].values[3]) : null;
        const tooltipContent = row.values[0].value;
        return /*#__PURE__*/_jsx(FunnelBarRow, {
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(Box, {
              grow: 1,
              basis: "auto",
              blockSize: "95%",
              children: /*#__PURE__*/_jsxs(Grid, {
                onClick: this.handleDrilldown.bind(this, row, row.values[1].key),
                marginBlock: "100",
                marginInline: "0",
                blockSize: "95%",
                children: [/*#__PURE__*/_jsx(GridItem, {
                  size: 4,
                  display: "flex",
                  backgroundColor: "fill-tertiary-hover",
                  alignItems: "center",
                  children: /*#__PURE__*/_jsx(Tooltip, {
                    title: tooltipContent,
                    disabled: !tooltipContent,
                    children: /*#__PURE__*/_jsx(TruncateString, {
                      children: row.values[0].value
                    })
                  })
                }), /*#__PURE__*/_jsx(GridItem, {
                  size: 8,
                  position: "relative",
                  borderRadius: "100",
                  padding: "200",
                  backgroundColor: "fill-tertiary-hover",
                  children: this.renderFunnelBar(ratio, colors[index], formatted)
                })]
              }, row.key)
            }), countOfStages > 1 && hasConversionSteps && /*#__PURE__*/_jsx(Box, {
              grow: 1,
              basis: 220,
              blockSize: "95%",
              children: /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "row",
                blockSize: "95%",
                children: [(primaryChevron || hasConversionSteps) && /*#__PURE__*/_jsx(ConversionColumn, {
                  onClick: this.handleDrilldown.bind(this, row, row.values[2].key),
                  children: primaryChevron
                }), (secondaryChevron || hasConversionSteps && secondaryChevronProperty) && /*#__PURE__*/_jsx(ConversionColumn, {
                  onClick: this.handleDrilldown.bind(this, row, row.values[3].key),
                  children: secondaryChevron
                })]
              })
            })]
          })
        }, index);
      });
    };
    this.renderHeader = visualization => {
      const datasetKey = visualization.get('dataset');
      const dataset = visualization.getIn(['data', datasetKey]);
      const filterProperty = visualization.get('filterProperty');
      const valueProperty = visualization.get('valueProperty');
      const propertyLabel = dataset.getIn(['properties', filterProperty, 'label']);
      const valueLabel = dataset.getIn(['properties', valueProperty, 'label']);
      const primaryChevronProperty = visualization.get('primaryChevronProperty');
      const secondaryChevronProperty = visualization.get('secondaryChevronProperty');
      const stepwiseConversionLabel = dataset.getIn(['properties', primaryChevronProperty, 'label'], null);
      const cumulativeConversionLabel = dataset.getIn(['properties', secondaryChevronProperty, 'label'], null);
      const countOfStages = dataset.get('data').size;
      const secondaryChevronTooltipKey = chevronTooltipLangKey[secondaryChevronProperty];
      return /*#__PURE__*/_jsx(FunnelBarRow, {
        header: true,
        children: /*#__PURE__*/_jsxs(Flex, {
          flexGrow: 1,
          children: [/*#__PURE__*/_jsx(Box, {
            grow: 1,
            basis: "auto",
            children: /*#__PURE__*/_jsxs(Grid, {
              flexGrow: 1,
              children: [/*#__PURE__*/_jsx(GridItem, {
                size: 4,
                children: /*#__PURE__*/_jsx(Text, {
                  typography: "heading-50",
                  color: "text-core-subtle",
                  marginBlockEnd: "400",
                  children: propertyLabel
                })
              }), /*#__PURE__*/_jsx(GridItem, {
                size: 8,
                children: /*#__PURE__*/_jsx(Text, {
                  typography: "heading-50",
                  color: "text-core-subtle",
                  marginBlockEnd: "400",
                  textAlign: "end",
                  children: valueLabel
                })
              })]
            })
          }), countOfStages > 1 && (stepwiseConversionLabel || cumulativeConversionLabel) && /*#__PURE__*/_jsx(Box, {
            children: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "row",
              children: [stepwiseConversionLabel && /*#__PURE__*/_jsx(ConversionColumn, {
                children: /*#__PURE__*/_jsx(Text, {
                  textAlign: "center",
                  typography: "heading-50",
                  color: "text-core-subtle",
                  marginBlockEnd: "400",
                  children: stepwiseConversionLabel
                })
              }), cumulativeConversionLabel && /*#__PURE__*/_jsx(ConversionColumn, {
                children: /*#__PURE__*/_jsxs(Text, {
                  textAlign: "center",
                  typography: "heading-50",
                  color: "text-core-subtle",
                  marginBlockEnd: "400",
                  children: [cumulativeConversionLabel, secondaryChevronTooltipKey && /*#__PURE__*/_jsx(Tooltip, {
                    title: I18n.text(secondaryChevronTooltipKey),
                    children: /*#__PURE__*/_jsx(IconHolder, {
                      icon: InfoIcon
                    })
                  })]
                })
              })]
            })
          })]
        })
      });
    };
  }
  render() {
    const {
      visualization
    } = this.props;
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      paddingInline: "400",
      children: [this.renderHeader(visualization), this.renderList(visualization)]
    });
  }
}
export const Funnel = props => {
  const theme = useTheme();
  const getTrellisTokenHexValue = tokenKey => theme.color[tokenKey];
  const themeColors = {
    startColor: getTrellisTokenHexValue('reporting-fill-chart-default-light-orange'),
    endColor: getTrellisTokenHexValue('reporting-fill-chart-default-dark-purple')
  };
  return /*#__PURE__*/_jsx(FunnelClassComponent, Object.assign({}, props, {
    themeColors: themeColors
  }));
};